import { GA4selectedReview } from "@/utils/analytics/analytics";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useCallback } from "react";
import { AppDispatch } from "@/redux/store";
import { ReviewClickEvent } from "@/types/reviewTypes";
import {
  TrustPilotBusinessType,
  TrustPilotReviewType,
} from "@/api/reviewTrustPilotApi";
import {
  selectDisableFormTrustPilot,
  reviewTrustPilotActions,
  selectSelectedCompanyTrustPilot,
} from "@/redux/slice/ReviewTrustPilotSlice";
import type { RootState } from "@/redux/store";
import { FUNNEL_TRUST_PILOT } from "@/types/funnelTypes";

export function useSelectReviewsTrustPilot() {
  const dispatch: AppDispatch = useDispatch();
  const store = useStore<RootState>();
  const disableForm: boolean = useSelector(selectDisableFormTrustPilot);

  const handleChosenItem = useCallback(
    (reviewId: string, event: ReviewClickEvent) => {
      if (disableForm) return;
      dispatch(
        reviewTrustPilotActions.setToggleItem({
          reviewId,
          isShiftUp: event.shiftKey,
        }),
      );
      GA4selectedReview(
        selectSelectedCompanyTrustPilot(store.getState())?.userSelectedReviews
          ?.length ?? 0,
        FUNNEL_TRUST_PILOT,
      );
    },
    [disableForm, dispatch, store],
  );

  /**
   * The persistence cart logic has been moved to
   * @see {@link listenerMiddleware}
   */
  const handleRemoveReview = useCallback(
    (review: TrustPilotReviewType, company: TrustPilotBusinessType) => {
      dispatch(
        reviewTrustPilotActions.removeReviewByCompany({ review, company }),
      );
    },
    [dispatch],
  );

  /**
   * The persistence cart logic has been moved to
   * @see {@link listenerMiddleware}
   */
  const handleRemoveAllSelectedReviewsByCompany = useCallback(
    (company: TrustPilotBusinessType) => {
      dispatch(
        reviewTrustPilotActions.removeAllSelectedReviewsByCompany({
          targetBusiness: company,
        }),
      );
    },
    [dispatch],
  );

  return {
    handleChosenItem,
    handleRemoveAllSelectedReviewsByCompany,
    handleRemoveReview,
  };
}

export function handleFilteredReviewsTrustPilot(
  items: TrustPilotReviewType[],
  userReviews?: TrustPilotReviewType[],
) {
  const reviews = items.filter((it) => it.isChosenItem);

  let newReviews = [...reviews];

  userReviews?.forEach((it) => {
    const targetReviewRaw = items.find((r) => r.id === it.id);

    if (!targetReviewRaw) {
      newReviews.push(it);
    }
  });

  return newReviews;
}

export function filterReviewsTrustPilot(
  selectedCompany: TrustPilotBusinessType,
  items: TrustPilotReviewType[],
): TrustPilotReviewType[] | [] | undefined {
  // Get all currently selected reviews from items (includes bulk/shift selection)
  const selectedFromItems = items.filter((it) => it.isChosenItem);

  // Get reviews from persistent cart that are not in current items
  // (these are reviews from previous sessions that haven't been loaded yet)
  const persistentCartReviews =
    selectedCompany.userSelectedReviews?.filter(
      (userReview) => !items.find((item) => item.id === userReview.id),
    ) || [];

  // Combine both: current selections + persistent cart reviews
  return [...selectedFromItems, ...persistentCartReviews];
}
